import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/ui/auth/AuthContext'
import { BACKEND_ENABLED } from '@/infrastructure/config/featureFlags'

/**
 * Small auth widget for the global header.
 *
 *   - loading        — empty (no flicker; the badge is decorative).
 *   - anonymous      — a "Sign in" link.
 *   - authenticated  — the email + a discreet "Logout" button.
 *
 * The widget never blocks the offline review surface; it's an opt-in
 * affordance per PRD #63 / #64.
 */
export function AuthBadge() {
  const { t } = useTranslation()
  const { state, signOut } = useAuth()

  // Backend disabled: the auth surface is fully hidden — no Sign in
  // link, no email pill, no logout button. The header collapses to
  // theme/font/locale switchers only.
  if (!BACKEND_ENABLED) return null

  if (state.status === 'loading') {
    return <span aria-hidden="true" style={{ width: '4rem' }} />
  }

  if (state.status === 'anonymous') {
    return (
      <Link to="/login" className="markfy-eyebrow-link" style={{ opacity: 0.7 }}>
        {t('auth.signIn.action')}
      </Link>
    )
  }

  return (
    <span className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-muted)' }}>
      <span title={state.user.email}>{state.user.email}</span>
      <button
        type="button"
        onClick={() => void signOut()}
        className="markfy-action-link"
        style={{ padding: 0 }}
      >
        {t('auth.logout')}
      </button>
    </span>
  )
}
